import { defaultCurrency } from "../config/currencies";
import { type Attribute } from "../interfaces/attribute";
import { 
  type Product, 
  type ProductImage, 
  type ProductVariant,  
} from "../interfaces/product";
import { ROUTES } from "../routes";
import { sort } from "./array";
import { isBetweenDates, isLessThanDays, toISODateString } from "./date";
import { calculateDiscountPercentage, isFloat } from "./number";
import { decodeSpecialChars } from "./string";

/*function filterProductsOfCategory(products: Product[], categoryName: string) {
  return products.filter(product => (
    product.category?.name?.toLowerCase() === categoryName.toLowerCase()
  ));
}*/

export function getFeaturedImage(images: ProductImage[]) {
  let featuredImage = images?.find(image => image.is_featured);
  
  if(!featuredImage) {
    featuredImage = images?.find(image => image.displayOrder === 1);
  }
  
  if(!featuredImage) {
    featuredImage = images?.[0];
  }

  return featuredImage ?? { url: getDefaultProductImageUrl() };
}

export function getImageHost(image: ProductImage) {
  if(image.url.startsWith("data:image/")) {
    return "disk";
  }

  if(image.url.startsWith("https://ik.imagekit.io")) {
    return "imagekit";
  }

  return "cloudinary";
}

/*function normalizeApiPriceForDisplay(price) {
  return (
    price
      ? Math.floor(Number(price) / 100)
      : 0.00
  );
}*/


export function compareProducts(products: Product[]) {
  if(!products || products.length < 2) {
    return "Please select at least two products to compare.";
  }

  const firstProduct = products[0];
  const comparisonResult: Record<string, { values: string[], different: boolean }> = {};

  for(const key in firstProduct) {
    comparisonResult[key] = {
      values: [],
      different: false,
    };

    for(const product of products) {
      comparisonResult[key].values.push(product[key]);
    }

    if(new Set(comparisonResult[key].values).size > 1) {
      comparisonResult[key].different = true;
    }
  }

  return comparisonResult;
}

export function generateRatingTitle(rating: number, rawRating: number) {
  let ratingTitle: string;

  if(rating > 0) {
    ratingTitle = `Average Rating ${rawRating}`;
  } else {
    ratingTitle = "No reviews yet";
  }

  return ratingTitle;
}

export function getComparisonFields(product: Product, possibleFields: { key: string, label: string }[]): { key: string, label: string }[] {
  const comparisonFields: { key: string, label: string }[] = [];

  for(const { key, label } of possibleFields) {
    if(key in product) {
      comparisonFields.push({ key, label });
    }
  }

  return comparisonFields;
}

export function getProductUrl(product: Pick<Product, "id">) {
  return ROUTES.PRODUCT.replace(":productId", product.id ?? "");
}

export function getBrandUrl(brand: { name: string }) {
  return ROUTES.BRAND.replace(":brandName", brand.name?.toLowerCase() ?? "");
}

export function getCategoryUrl(category: { name: string }) {
  return ROUTES.CATEGORY.replace(":categoryName", category.name ?? "");
}

export function parseBrandForDisplay(brand: { id: string, name: string }) {
  const brandName = decodeSpecialChars(brand.name ?? "");

  return {
    ...brand,
    id: brand.id,
    name: brandName,
    value: `${brand.id}:${brandName}`,
    url: getBrandUrl(brand),
  };
}

export function parsePriceInfo(price: number, discountedPrice: number) {
  let originalPrice: number;
  let sellingPrice: number;
  let discountPercentage: number;
  const returnData: { sellingPrice?: number; originalPrice?: number; discountPercentage?: number } = {};

  if(discountedPrice && discountedPrice !== price) {
    originalPrice = price;
    sellingPrice = discountedPrice;
    discountPercentage = calculateDiscountPercentage(originalPrice, sellingPrice);

    returnData.originalPrice = originalPrice;
    returnData.sellingPrice = sellingPrice;
    returnData.discountPercentage = discountPercentage;
  } else {
    sellingPrice = price;

    returnData.sellingPrice = sellingPrice;
  }

  return returnData;

  /*return [
    originalPrice,
    sellingPrice,
    discountPercentage
  ];*/
}

export function parseProductForDisplay(product): Product | null {
  if(!product) {
    return null;
  }

  // Product variants have a "product" property
  // pointing to the parent product
  const parent = product?.product;
  const attributes = product.attributes;
  const brand = attributes.find(a => a.name === "Brand");
  const featuredImage = getFeaturedImage(product.images);

  return { 
    id                 : product.id, 
    type               : "Product", 
    name               : decodeSpecialChars(product.name ?? parent?.name), 
    sku                : product.sku, 
    url                : getProductUrl(product),   
    description        : decodeSpecialChars(product.description ?? parent?.description), 
    shortDescription   : decodeSpecialChars(product.short_description ?? parent?.short_description),
    business           : { ...(product.business ?? parent?.business), name: decodeSpecialChars((product.business ?? parent?.business ?? {}).name) },
    category           : { ...(product.category ?? parent?.category), name: decodeSpecialChars((product.category ?? parent?.category ?? {}).name) },
    status             : product.archived ? "Archived" : product.status, 
    inventory          : product.inventory,
    rating             : { ...product.rating, average: formatRating(product.rating?.average ?? 0) }, 
    price              : product.price, 
    discountedPrice    : product.discounted_price, 
    currencyCode       : product.currency_code ?? defaultCurrency.code,
    features           : decodeSpecialChars(product.features ?? parent.features), 
    brand              : { id: brand?.id ?? "", name: brand?.value, url: getBrandUrl({ name: brand?.value }) },
    weight             : attributes.find(a => a.name === "Weight")?.value, 
    dimensions         : attributes.find(a => a.name === "Dimensions")?.value,
    attributes         : attributes.filter(a => !["Dimensions", "Weight"].includes(a.name)).map(parseAttributeForDisplay),
    featuredImage      : featuredImage, 
    images             : sortImagesByDisplayOrder(product.images?.filter(i => i.url !== featuredImage.url)), 
    isDownloadable     : product.is_downloadable, 
    isVirtual          : product.is_virtual, 
    archived           : product.archived, 
    allowPayOnDelivery : product.allow_pay_on_delivery,   
    stockStatus        : product.stock_status, 
    isAvailable        : isInStock(product), 
    isNew              : isNew(product),
    isOnSale           : isOnSale(product),
    saleStartDate      : product.sale_start_date, 
    saleEndDate        : product.sale_end_date, 
    shippingClass      : product.shipping_class, 
    taxClass           : product.tax_class, 
    processingTime     : product.processing_time, 
    cancellationPolicy : decodeSpecialChars(product.cancellation_policy ?? parent.cancellation_policy), 
    refundPolicy       : decodeSpecialChars(product.refund_policy ?? parent.refund_policy), 
    returnPolicy       : decodeSpecialChars(product.return_policy ?? parent.return_policy), 
    shippingPolicy     : decodeSpecialChars(product.shipping_policy ?? parent.shipping_policy), 
    purchaseNote       : decodeSpecialChars(product.purchase_note ?? parent.purchase_note),
    variants           : (product.variants || []).map(v => parseProductVariantForDisplay(v, product))
  };
}

export function parseProductVariantForDisplay(variant, parent: Product): ProductVariant | null {
  if(!variant) {
    return null;
  }
  
  let { images } = variant;
  const { attributes } = variant;

  if(images.length === 0) {
    images = images = parent.images;
  }

  const featuredImage = getFeaturedImage(images);

  return {
    id                 : variant.id,
    sku                : variant.sku,
    name               : decodeSpecialChars(parent.name),
    url                : `${getProductUrl(parent)}?v=${variant.id}`,
    type               : "ProductVariant",
    price              : variant.price,
    discountedPrice    : variant.discounted_price,
    currencyCode       : variant.currency_code ?? defaultCurrency.code,
    status             : variant.status,
    description        : decodeSpecialChars(variant.description ?? ""),
    isDownloadable     : variant.is_downloadable,
    isVirtual          : variant.is_virtual,
    archived           : variant.archived,
    allowPayOnDelivery : variant.allow_pay_on_delivery,
    inventory          : variant.inventory,
    stockStatus        : variant.stock_status ?? "",
    isAvailable        : isInStock(variant), 
    isNew              : isNew(variant),
    isOnSale           : isOnSale(variant),
    saleStartDate      : variant.sale_start_date,
    saleEndDate        : variant.sale_end_date, 
    weight             : attributes.find(a => a.name === "Weight")?.value, 
    dimensions         : attributes.find(a => a.name === "Dimensions")?.value,
    attributes         : attributes.filter(a => !["Dimensions", "Weight"].includes(a.name)).map(parseAttributeForDisplay), 
    featuredImage      : featuredImage, 
    images             : sortImagesByDisplayOrder(images?.filter(i => i.url !== featuredImage.url)),
    shippingClass      : variant.shipping_class ?? "",
    taxClass           : variant.tax_class ?? "",
    processingTime     : variant.processing_time ?? "",
    rating             : variant.rating, 
    product            : parent,
  };
}

export function rawRating(value: number): number {
  return (isFloat(value) ? Number(value.toFixed(1)) : value);
}

/*export function parseProductForDisplay(product) {
  // Product variants have a "product" property
  // pointing to the parent product
  const parent = product?.product;
  const parsedAttributes = parseProductAttributes(parent || product);
  const productImages = getProductImages(product);

  return {
    ...product,
    id: product.id,
    name: decodeSpecialChars(product.name || parent?.name),
    description: decodeSpecialChars(product.description || parent?.description),
    price: product.price,
    discountedPrice: product.discounted_price,
    currencyCode: defaultCurrency.code,
    url: getProductUrl(product),
    business: product.business || parent?.business,
    sku: product.sku,
    type: parent ? "ProductVariant" : "Product",
    category: product.category || parent?.category,
    //images: product.images,
    imageUrl: productImages[0].url,
    inventory: product.inventory,
    stockStatus: product.stock_status,
    allowPayOnDelivery: product.allow_pay_on_delivery,
    rawRating: rawRating(product.rating?.average),
    rating: formatRating(product.rating?.average),
    totalReviews: product.rating?.reviews,
    images: productImages.map(image => ({
      large: image.url,
      thumbnail: image.thumbnailUrl || image.url
    })),

    ...parsedAttributes,
    brand: getProductBrand(product),
  };
}*/

/*export function parseProductFromAdvancedSearchForDisplay(product) {
  //const { attributes } = product;
  const productImages = getProductImages(product);
  const parsedAttributes = parseProductAttributes(product);

  return {
    ...product,
    name: decodeSpecialChars(product.name),
    description: decodeSpecialChars(product.description),
    discountedPrice: product.discounted_price,
    currencyCode: defaultCurrency.code,
    url: getProductUrl(product),
    business: product.business,
    sku: product.sku,
    type: "Product",
    stockStatus: product.stock_status,
    allowPayOnDelivery: product.allow_pay_on_delivery,
    brands: product.brands,
    imageUrl: productImages[0].url,
    rawRating: rawRating(product.rating.average),
    rating: formatRating(product.rating.average),
    totalReviews: product.rating.reviews,
    images: productImages.map(image => ({
      large: image.url,
      thumbnail: image.thumbnailUrl || image.url
    })),
    /*attributes: attributes.reduce((map, productAttribute) => {
      //const attributeId = productAttribute.attribute.id;
      const attributeName = productAttribute.attribute.name;
      const attributeValue = productAttribute.value;
      const attributeKey = attributeName;

      (map[attributeKey] = map[attributeKey] || []).push(attributeValue);

      return map;
    }, {}),* /
    ...parsedAttributes,
    brand: getProductBrand(product),
  };
}*/

/*export function parseProductVariantFromAdvancedSearchForDisplay(variant) {
  const { product, attributes } = variant;
  const variantImages = variant?.images?.length > 0 ? variant.images : product.images;

  return {
    //...variant,
    id: variant.id, // This must never be the parent ID, it must be the variant's
    name: decodeSpecialChars(product.name),
    description: decodeSpecialChars(product.description),
    price: variant.price,
    discountedPrice: variant.discounted_price,
    currencyCode: defaultCurrency.code,
    url: getProductUrl(variant),
    business: product.business,
    sku: variant.sku,
    type: "ProductVariant",
    brands: product.brands,
    category: variant.category || product.category,
    imageUrl: variantImages[0].url,
    inventory: variant.inventory,
    rawRating: rawRating(variant.rating.average),
    rating: formatRating(variant.rating.average),
    totalReviews: variant.rating.reviews,
    images: variantImages.map(image => ({
      large: image.url,
      thumbnail: image.thumbnailUrl || image.url
    })),
    attributes: attributes.reduce((map, variantAttribute) => {
      //const attributeId = variantAttribute.attribute.id;
      const attributeName = variantAttribute.attribute.name;
      const attributeValue = variantAttribute.value;
      const attributeKey = attributeName;

      (map[attributeKey] = map[attributeKey] || []).push(attributeValue);

      return map;
    }, {}),
  };
}*/


// Helper functions
function formatRating(value: number) {
  return Math.round(value);
}

function getDefaultProductImageUrl() {
  return "images/product/product-placeholder.svg";
}

function isInStock(product: { stock_status: string; inventory: { quantity: number; }}) {
  let isAvailable = true;
  const isInStock = product.stock_status === "In stock";

  if(product.inventory) {
    isAvailable = isInStock && product.inventory.quantity > 0;
  } else {
    isAvailable = isInStock;
  }

  return isAvailable;
}

function isNew(product: { created_at: string }) {
  return isLessThanDays(product.created_at, 7);
}

function isOnSale(product: { sale_start_date?: string; sale_end_date?: string; }) { 
  const now = toISODateString(new Date());
  const { sale_start_date, sale_end_date } = product;

  const startDate = sale_start_date ?? now;
  const endDate = sale_end_date ?? now;

  return isBetweenDates(startDate, endDate, now);
}

function parseAttributeForDisplay(attribute): Attribute | null {
  if(!attribute) {
    return null;
  }

  return {
    ...attribute, 
    options: attribute.options ?? [],
  }
}

function sortImagesByDisplayOrder(images: ProductImage[]) {
  return sort(images, { by: "displayOrder", order: "asc" });
}

/*function getProductBrand(product) {
  const parent = product?.product;
  const parsedAttributes = parseProductAttributes(parent || product);

  let oldBrandMethod: { name: string } | undefined;
  
  if(product?.brands?.length > 0) {
    oldBrandMethod = product?.brands[0];
  } else if(parent?.brands?.length > 0) {
    oldBrandMethod = parent?.brands[0];
  }

  return parsedAttributes.brand || oldBrandMethod?.name;
}*/

/*function getProductImages(product): Array<{ url: string, thumbnailUrl: string }> {
  const parent = product?.product;
  let productImages: Array<{ url: string, thumbnailUrl: string}>;

  if(product.images?.length > 0) {
    productImages = product.images;
  } else if(parent?.images?.length > 0) {
    productImages = parent.images;
  } else {
    const defaultProductImageUrl = getDefaultProductImageUrl();

    productImages = [{ url: defaultProductImageUrl, thumbnailUrl: defaultProductImageUrl }];
  }

  return productImages;
}*/

/*function parseProductAttributes(product) {
  const complexAttributes = product.attributes.filter(isComplexAttribute);
  const regularAttributes = product.attributes.filter(attribute => !isComplexAttribute(attribute));

  const allAttributes = [
    ...regularAttributes,
    ...(complexAttributes.map(prodAttribute => ({ 
      name: prodAttribute.attribute.name, 
      value: prodAttribute.value.value 
    }))),
    
  ];

  //return product.attributes.reduce((parsedAttributes, attribute) => {
  return allAttributes.reduce((parsedAttributes, attribute) => {
    if(["Brand", "Color", "Size"].includes(attribute.name)) {
      parsedAttributes[attribute.name.toLowerCase()] = attribute.value.name; 
    } else {
      parsedAttributes[attribute.name.toLowerCase()] = attribute.value;
    }

    return parsedAttributes;
  }, {});

  /*const attributes = product.attributes; 
  const parsedAttributes = {};

  for(const attribute of attributes) {
    parsedAttributes[attribute.name.toLowerCase()] = attribute.value;
  }

  return parsedAttributes;* /

  function isComplexAttribute(attribute) {
    // Attributes coming from advanced product search
    return ("attribute" in attribute && "value" in attribute);
  }
}*/
